/**
 * Payments-matrix grouping — pure functions (unit tested in
 * `tests/unit/matrix.unit.spec.ts`), no React and no API imports.
 */

import type { PaymentEvent, Subscription } from '../api/types';

/** `2026-08-07T16:49:00.000Z` -> `2026-08`. */
export function monthKeyOf(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The last `count` months ending with the current one, oldest first.
 * `anchor` is injectable so tests stay deterministic.
 */
export function monthWindow(count: number, now: Date = new Date()): string[] {
  const keys: string[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1));
    keys.push(monthKeyOf(date));
  }
  return keys;
}

/** A month window that ends with `monthKey` instead of the current month. */
export function monthWindowEnding(count: number, monthKey: string): string[] {
  const [year, month] = monthKey.split('-').map(Number);
  if (!year || !month) return monthWindow(count);
  return monthWindow(count, new Date(Date.UTC(year, month - 1, 1)));
}

/**
 * The `count` months *after* the current one, oldest first. The matrix's
 * "Ending with" select offers them so a window can reach past today — that is
 * what makes the forecasted-spendings section worth anything more than the
 * current month.
 */
export function upcomingMonths(count: number, now: Date = new Date()): string[] {
  const keys: string[] = [];
  for (let offset = 1; offset <= count; offset += 1) {
    keys.push(monthKeyOf(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1))));
  }
  return keys;
}

export interface MatrixCellEvent {
  event: PaymentEvent;
  monthKey: string;
}

export interface MatrixRow {
  subscription: Subscription;
  /** `monthKey` -> events landing in that month. */
  cells: Map<string, MatrixCellEvent[]>;
  paidByCurrency: Record<string, number>;
  rubTotal: number;
}

export interface MatrixGroup {
  name: string;
  rows: MatrixRow[];
}

export interface MatrixTotals {
  /** Grand totals for the whole window, per currency and in rubles. */
  paidByCurrency: Record<string, number>;
  rubTotal: number;
  eventsCount: number;
  /** Per-month grand total, in rubles. */
  byMonth: Record<string, number>;
}

export interface MatrixResult {
  groups: MatrixGroup[];
  /** Rows outside any group, last. */
  ungrouped: MatrixRow[];
  totals: MatrixTotals;
}

function emptyRow(subscription: Subscription): MatrixRow {
  return { subscription, cells: new Map(), paidByCurrency: {}, rubTotal: 0 };
}

function addAmount(bucket: Record<string, number>, currency: string, amount: number): void {
  bucket[currency] = round2((bucket[currency] ?? 0) + amount);
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Groups subscriptions by their vendor group (group name order preserved as
 * the API returns it) and buckets the events into the month window. Events
 * outside the window are ignored — the caller fetches exactly the window.
 */
export function buildMatrix(
  subscriptions: Subscription[],
  events: PaymentEvent[],
  months: string[],
): MatrixResult {
  const allowed = new Set(months);
  const byId = new Map<string, MatrixRow>();
  for (const subscription of subscriptions) byId.set(subscription.id, emptyRow(subscription));

  const totals: MatrixTotals = { paidByCurrency: {}, rubTotal: 0, eventsCount: 0, byMonth: {} };

  for (const event of events) {
    const row = byId.get(event.subscriptionId);
    if (!row) continue;
    const key = monthKeyOf(event.occurredAt);
    if (!allowed.has(key)) continue;

    const bucket = row.cells.get(key) ?? [];
    bucket.push({ event, monthKey: key });
    row.cells.set(key, bucket);

    addAmount(row.paidByCurrency, event.paidCurrency, event.paidAmount);
    if (event.rubTotal !== null) row.rubTotal = round2(row.rubTotal + event.rubTotal);

    addAmount(totals.paidByCurrency, event.paidCurrency, event.paidAmount);
    if (event.rubTotal !== null) totals.rubTotal = round2(totals.rubTotal + event.rubTotal);
    totals.byMonth[key] = round2((totals.byMonth[key] ?? 0) + (event.rubTotal ?? 0));
    totals.eventsCount += 1;
  }

  const rows = [...byId.values()];
  const groupOrder: string[] = [];
  const grouped = new Map<string, MatrixRow[]>();
  const ungrouped: MatrixRow[] = [];

  for (const row of rows) {
    const name = row.subscription.group?.name;
    if (!name) {
      ungrouped.push(row);
      continue;
    }
    if (!grouped.has(name)) {
      grouped.set(name, []);
      groupOrder.push(name);
    }
    grouped.get(name)?.push(row);
  }

  // Rows with spend but no subscription row (deleted subscription) are dropped
  // on purpose: the matrix is subscription-shaped.

  return {
    groups: groupOrder.map((name) => ({ name, rows: grouped.get(name) ?? [] })),
    ungrouped,
    totals,
  };
}

/** `true` when the row carries a two-step (conversion + payment) event. */
export function isTwoStep(event: PaymentEvent): boolean {
  if (event.flow === 'TWO_STEP') return true;
  const types = new Set(event.transactions.map((txn) => txn.type));
  return types.has('CONVERSION') && types.has('PAYMENT');
}
